import angular from 'angular';
import {
    IMovimentacaoFormBindings,
    IMovimentacaoFormModel,
    IMovimentacaoFormState,
    TMovimentacaoFormLayout,
    TMovimentacaoFormMode
} from './movimentacoes-movimentacao-form.model';
import { IMovimentacaoPeriodo } from '../../../models/movimentacoes-periodos.service.model';
import {
    buildMovimentacaoDataPagamentoWindow,
    createEmptyMovimentacaoForm,
    isMovimentacaoDataPagamentoEnabled
} from '../../helpers/movimentacoes-form.helpers';
import {
    isMovimentacaoEventoDecimal,
    isMovimentacaoEventoDias,
    isMovimentacaoEventoHoras,
    isMovimentacaoEventoMonetario,
    MOVIMENTACAO_REFERENCIA_MAX_LENGTH
} from '../../../helpers/movimentacoes-value.helpers';

export class MovimentacoesMovimentacaoFormController implements angular.IController, IMovimentacaoFormBindings {
    static $inject = ['$scope'];

    public model: IMovimentacaoFormModel;
    public selectedPeriod: IMovimentacaoPeriodo | null = null;
    public formRef?: angular.IFormController;
    public disabled = false;
    public mode?: TMovimentacaoFormMode;
    public layout?: TMovimentacaoFormLayout;
    public colaboradorFotoUrl: string | null = null;
    public onValidityChange?: (args: { state: IMovimentacaoFormState }) => void;

    public readonly referenciaMaxLength = MOVIMENTACAO_REFERENCIA_MAX_LENGTH;
    public vigenciaFinalInvalida = false;
    public dataPagamentoEnabled = isMovimentacaoDataPagamentoEnabled();
    public dataPagamentoMinDate: string | null = null;
    public dataPagamentoMaxDate: string | null = null;
    public meses = [
        { value: 1, label: 'Janeiro' },
        { value: 2, label: 'Fevereiro' },
        { value: 3, label: 'Março' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Maio' },
        { value: 6, label: 'Junho' },
        { value: 7, label: 'Julho' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Setembro' },
        { value: 10, label: 'Outubro' },
        { value: 11, label: 'Novembro' },
        { value: 12, label: 'Dezembro' }
    ];
    public anos: number[] = [];

    constructor(public $scope: angular.IScope) { }

    public $onInit(): void {
        this.ensureModel();
        this.syncDataPagamentoWindow();
        this.buildAnos();
        this.setupWatchers();
    }

    public $onChanges(changes: angular.IOnChangesObject): void {
        if (changes.selectedPeriod) {
            this.syncDataPagamentoWindow();
        }
    }

    public isDrawerLayout(): boolean {
        return this.layout === 'drawer';
    }

    public isQuickAddCreateLayout(): boolean {
        return this.mode === 'create' && !this.isDrawerLayout();
    }

    public isDrawerEditLayout(): boolean {
        return this.mode === 'edit' && this.isDrawerLayout();
    }

    public shouldShowReadonlyColaborador(): boolean {
        return this.isDrawerEditLayout();
    }

    public shouldUseRichVigenciaOptions(): boolean {
        return this.isQuickAddCreateLayout() || this.isDrawerEditLayout();
    }

    public getFieldColumnClass(): string {
        return this.isDrawerLayout()
            ? 'col-md-12 col-sm-12'
            : 'col-md-4 col-sm-12';
    }

    public getColaboradorFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return 'col-md-6 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getLotacaoFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return 'col-md-6 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getRubricaFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return 'col-md-4 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getValorFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return 'col-md-2 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getReferenciaFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return this.dataPagamentoEnabled
                ? 'col-md-4 col-sm-12'
                : 'col-md-6 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getDataPagamentoFieldClass(): string {
        if (this.isQuickAddCreateLayout()) {
            return 'col-md-2 col-sm-12';
        }

        return this.getFieldColumnClass();
    }

    public getVigenciaFieldsContainerClass(): string {
        return this.isDrawerLayout()
            ? 'mvm-movimentacao-form__vigencia-fields mvm-movimentacao-form__vigencia-fields--stacked'
            : 'mvm-movimentacao-form__vigencia-fields';
    }

    public getVigenciaLabelText(): string {
        return this.shouldUseRichVigenciaOptions()
            ? 'Vigência do lançamento'
            : 'Vigência';
    }

    public setVigencia(vigencia: 1 | 2 | 3): void {
        this.model.vigencia = vigencia;
        if (vigencia !== 3) {
            this.model.vigenciamesfinal = null;
            this.model.vigenciaanofinal = null;
            this.vigenciaFinalInvalida = false;
        }
        this.emitState();
    }

    public isEventoHoras(): boolean {
        return isMovimentacaoEventoHoras(this.model?.evento);
    }

    public isEventoDias(): boolean {
        return isMovimentacaoEventoDias(this.model?.evento);
    }

    public isEventoMonetario(): boolean {
        return isMovimentacaoEventoMonetario(this.model?.evento);
    }

    public isEventoDecimal(): boolean {
        return isMovimentacaoEventoDecimal(this.model?.evento);
    }

    public onVigenciaChange(): void {
        this.emitState();
    }

    public getColaboradorLabel(): string {
        return this.model?.trabalhador?.nome || '-';
    }

    public getMatriculaLabel(): string {
        return this.model?.trabalhador?.codigo || '-';
    }

    private setupWatchers(): void {
        this.$scope.$watch(() => this.model, () => {
            this.emitState();
        }, true);
        this.$scope.$watch(() => this.formRef && this.formRef.$valid, () => {
            this.emitState();
        });

        if (this.mode === 'create') {
            this.$scope.$watch(() => this.model?.trabalhador, (current, previous) => {
                if (current === previous) {
                    return;
                }

                this.syncLotacaoFromTrabalhador();
            }, true);
        }
    }

    private emitState(): void {
        this.vigenciaFinalInvalida = this.isVigenciaThreeWithInvalidFinalPeriodo();
        const formValid = this.formRef ? this.formRef.$valid : false;
        const state: IMovimentacaoFormState = {
            valid: formValid && !this.vigenciaFinalInvalida,
            vigenciaFinalInvalida: this.vigenciaFinalInvalida
        };

        if (this.onValidityChange) {
            this.onValidityChange({ state });
        }
    }

    private ensureModel(): void {
        if (this.model) {
            return;
        }

        this.model = createEmptyMovimentacaoForm();
    }

    private syncLotacaoFromTrabalhador(): void {
        const lotacaoObj = this.model?.trabalhador?.lotacaoobj;
        this.model.lotacao = lotacaoObj || null;
    }

    private syncDataPagamentoWindow(): void {
        const { minDate, maxDate } = buildMovimentacaoDataPagamentoWindow(this.selectedPeriod);
        this.dataPagamentoMinDate = minDate;
        this.dataPagamentoMaxDate = maxDate;
    }

    private buildAnos(): void {
        const anoAtual = new Date().getFullYear();
        this.anos = [anoAtual, anoAtual + 1, anoAtual + 2];
    }

    private isVigenciaThreeWithInvalidFinalPeriodo(): boolean {
        if (this.model.vigencia !== 3 || !this.selectedPeriod) {
            return false;
        }

        if (!this.model.vigenciamesfinal || !this.model.vigenciaanofinal) {
            return false;
        }

        const finalAno = Number(this.model.vigenciaanofinal);
        const finalMes = Number(this.model.vigenciamesfinal);
        const periodoAno = Number(this.selectedPeriod.ano);
        const periodoMes = Number(this.selectedPeriod.mes);

        if (finalAno > periodoAno) {
            return false;
        }

        if (finalAno < periodoAno) {
            return true;
        }

        return finalMes <= periodoMes;
    }
}
